import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Mail, MessageCircle, ShieldCheck } from "lucide-react";

import {
    NOTICE_CHANNELS,
    allowsWhatsApp,
    channelChoice,
    type NoticeChannel,
} from "../../domain/noticeChannel";
import { summariseNotice, type TranslatableText } from "../../domain/noticeSummary";
import { translateSummary } from "./translateSummary";

type AudienceOption = "toPatient" | "toTherapist" | "toBoth";

export interface NotificationAudience {
    toPatient: boolean;
    toTherapist: boolean;
}

export type NotificationChannelControl =
    /** A notification with no WhatsApp message at all, such as a payment change. */
    | { kind: "fixedEmail" }
    | {
          kind: "choice";
          value: NoticeChannel;
          /** Whether this deployment can send this notification on WhatsApp. */
          whatsappAvailable: boolean;
          onChange: (channel: NoticeChannel) => void;
      };

interface NotificationPreferenceRowProps {
    /** Distinguishes this row's radio group from the others on the page. */
    name: string;
    title: string;
    description?: string;
    enabled: boolean;
    toPatient: boolean;
    toTherapist: boolean;
    disabled?: boolean;
    onEnabledChange: (enabled: boolean) => void;
    onAudienceChange: (audience: NotificationAudience) => void;
    /** The patient copy's channel. Omitted: no channel section, summarised as email. */
    channel?: NotificationChannelControl;
    /** When this notification goes out, as the first half of the summary line. Omitted: no summary. */
    when?: TranslatableText;
    /** Extra controls that only matter while the notification is sent. */
    children?: ReactNode;
}

const OPTIONS: { key: AudienceOption; audience: NotificationAudience }[] = [
    { key: "toPatient", audience: { toPatient: true, toTherapist: false } },
    { key: "toTherapist", audience: { toPatient: false, toTherapist: true } },
    { key: "toBoth", audience: { toPatient: true, toTherapist: true } },
];

// Literal keys, so the i18n parity guard can see them.
const CHANNEL_LABELS: Record<NoticeChannel, string> = {
    email: "notificationPreferences.channelEmail",
    whatsapp: "notificationPreferences.channelWhatsApp",
    email_and_whatsapp: "notificationPreferences.channelBoth",
};

function ChannelGlyph({ channel }: { channel: NoticeChannel }) {
    return (
        <span aria-hidden="true" className="flex items-center gap-1 text-content-secondary">
            {channel !== "whatsapp" && <Mail size={16} />}
            {channel !== "email" && <MessageCircle size={16} />}
        </span>
    );
}

/**
 * One notification a tool sends: whether it goes out, to whom, and on which
 * channel the patient's copy travels.
 *
 * Three questions in the order a therapist asks them. Who receives it only
 * appears once it is switched on — choosing recipients for a message that is
 * never sent is a question with no consequence — and the channel only once
 * the patient is among them.
 *
 * Recipients are one choice among three rather than two checkboxes: "nobody"
 * is already what the switch above says, so offering it twice invites the
 * contradiction of a notification that is on and reaches no one. A value
 * saved that way through the API still shows, as no selection and a warning.
 * The channel is one choice among three for the same reason: "neither email
 * nor WhatsApp" cannot be expressed.
 *
 * The privacy note sits beside the WhatsApp options, not only in a banner:
 * the choice is where a therapist might believe they are turning WhatsApp on
 * for someone, when only a patient who accepted it in their portal is ever
 * written there. Where WhatsApp cannot carry the notification, email is
 * stated as fact rather than offered as a choice that changes nothing.
 *
 * A summary line under the title says what is sent, when, to whom and how. It
 * describes the checkbox, the one control every row has, and is not a live
 * region: it is read when the therapist reaches the row, not on every change.
 */
export default function NotificationPreferenceRow({
    name,
    title,
    description,
    enabled,
    toPatient,
    toTherapist,
    disabled = false,
    onEnabledChange,
    onAudienceChange,
    channel,
    when,
    children,
}: NotificationPreferenceRowProps) {
    const { t } = useTranslation();
    const reachesNobody = enabled && !toPatient && !toTherapist;
    const legendId = `${name}-recipients-legend`;
    const summaryId = `${name}-summary`;
    const channelLegendId = `${name}-channel-legend`;
    const consentId = `${name}-channel-consent`;

    const whatsappAvailable = channel?.kind === "choice" && channel.whatsappAvailable;
    const shown = channelChoice({ enabled, toPatient, whatsappAvailable });

    return (
        <div
            role="group"
            aria-label={title}
            className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4 dark:border-gray-700"
        >
            <div className="flex flex-col gap-1">
                <label className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        className="mt-1"
                        checked={enabled}
                        disabled={disabled}
                        aria-label={title}
                        aria-describedby={when ? summaryId : undefined}
                        onChange={(event) => onEnabledChange(event.target.checked)}
                    />
                    <span className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-content-primary">{title}</span>
                        {description && (
                            <span className="text-content-tertiary">{description}</span>
                        )}
                    </span>
                </label>

                {when && (
                    <p id={summaryId} className="pl-7 text-sm text-content-secondary">
                        {translateSummary(
                            t,
                            summariseNotice({
                                enabled,
                                toPatient,
                                toTherapist,
                                // Only read when WhatsApp is available, which
                                // is also when the server sends the channel.
                                channel: channel?.kind === "choice" ? channel.value : "email",
                                whatsappAvailable,
                                when,
                            })
                        )}
                    </p>
                )}
            </div>

            {enabled && (
                <div className="flex flex-col gap-4 pl-7">
                    <div
                        role="radiogroup"
                        aria-labelledby={legendId}
                        className="flex flex-col gap-2 text-sm"
                    >
                        <span id={legendId} className="font-medium text-content-secondary">
                            {t("notificationPreferences.recipients")}
                        </span>
                        <div className="flex flex-wrap gap-x-5 gap-y-2">
                            {OPTIONS.map(({ key, audience }) => (
                                <label key={key} className="flex min-h-11 items-center gap-2">
                                    <input
                                        type="radio"
                                        name={`${name}-recipients`}
                                        checked={
                                            audience.toPatient === toPatient &&
                                            audience.toTherapist === toTherapist
                                        }
                                        disabled={disabled}
                                        onChange={() => onAudienceChange(audience)}
                                    />
                                    {t(`notificationPreferences.${key}`)}
                                </label>
                            ))}
                        </div>
                    </div>

                    {reachesNobody && (
                        <p role="alert" className="text-sm text-status-warning">
                            {t("notificationPreferences.nobodySelected")}
                        </p>
                    )}

                    {shown === "choose" && channel?.kind === "choice" && (
                        <div className="flex flex-col gap-2 text-sm">
                            <div role="radiogroup" aria-labelledby={channelLegendId} className="flex flex-col gap-2">
                                <span
                                    id={channelLegendId}
                                    className="font-medium text-content-secondary"
                                >
                                    {t("notificationPreferences.channel")}
                                </span>
                                <div className="flex flex-wrap gap-x-5 gap-y-2">
                                    {NOTICE_CHANNELS.map((option) => (
                                        <label
                                            key={option}
                                            className="flex min-h-11 items-center gap-2"
                                        >
                                            <input
                                                type="radio"
                                                name={`${name}-channel`}
                                                checked={channel.value === option}
                                                disabled={disabled}
                                                aria-describedby={
                                                    allowsWhatsApp(option) ? consentId : undefined
                                                }
                                                onChange={() => channel.onChange(option)}
                                            />
                                            <ChannelGlyph channel={option} />
                                            {t(CHANNEL_LABELS[option])}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <p id={consentId} className="flex items-start gap-2 text-content-secondary">
                                <ShieldCheck size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
                                {t("notificationPreferences.whatsappConsentShort")}
                            </p>
                        </div>
                    )}

                    {shown === "fixedEmail" && (
                        <p className="flex items-center gap-2 text-sm text-content-secondary">
                            <Mail size={16} aria-hidden="true" className="shrink-0" />
                            {t("notificationPreferences.channelFixedEmail")}
                        </p>
                    )}

                    {children}
                </div>
            )}
        </div>
    );
}
